import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  DocumentTextIcon,
  UserGroupIcon,
  ChartBarIcon,
  ArrowLeftIcon,
  InformationCircleIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

interface Vote {
  id: string;
  voter: {
    address: string;
    avatar: string;
  };
  choice: 'for' | 'against';
  votingPower: number;
  timestamp: Date;
}

const mockVotes: Vote[] = [
  { id: '1', voter: { address: '0x1234...5678', avatar: 'https://picsum.photos/seed/voter1/100/100' }, choice: 'for', votingPower: 125000, timestamp: new Date(Date.now() - 3600000) },
  { id: '2', voter: { address: '0xABCD...EFGH', avatar: 'https://picsum.photos/seed/voter2/100/100' }, choice: 'for', votingPower: 89000, timestamp: new Date(Date.now() - 7200000) },
  { id: '3', voter: { address: '0x9876...5432', avatar: 'https://picsum.photos/seed/voter3/100/100' }, choice: 'against', votingPower: 45000, timestamp: new Date(Date.now() - 10800000) },
  { id: '4', voter: { address: '0x5555...6666', avatar: 'https://picsum.photos/seed/voter4/100/100' }, choice: 'for', votingPower: 78000, timestamp: new Date(Date.now() - 14400000) },
  { id: '5', voter: { address: '0x7777...8888', avatar: 'https://picsum.photos/seed/voter5/100/100' }, choice: 'for', votingPower: 156000, timestamp: new Date(Date.now() - 18000000) },
  { id: '6', voter: { address: '0x2222...3333', avatar: 'https://picsum.photos/seed/voter6/100/100' }, choice: 'against', votingPower: 34000, timestamp: new Date(Date.now() - 21600000) },
];

const ProposalDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState<'details' | 'votes' | 'discussion'>('details');
  const [hasVoted, setHasVoted] = useState(false);

  // Mock proposal data
  const proposal = {
    id: id,
    title: 'Reduce Platform Fees to 2%',
    description: `
## Summary
This proposal seeks to reduce the base platform fee from 2.5% to 2% to make TEMPLUM more competitive and attract higher trading volume.

## Motivation
Current market conditions have led to reduced NFT trading activity across all platforms. By reducing our fees, we can:
- Attract more users from competing platforms
- Increase overall trading volume
- Generate more revenue despite lower percentage (volume > margin)
- Build stronger community loyalty

## Specification
The following changes would be implemented:
1. Base platform fee reduced from 2.5% to 2%
2. NDH staker discounts remain unchanged (up to 2.5% discount)
3. Platinum tier stakers continue to pay 0% fees
4. Changes effective 48 hours after proposal passes

## Implementation
The treasury multisig will update the fee parameters in the AuctionFactory contract. No contract upgrades are required as fee parameters are configurable.

## Risk Analysis
- **Revenue Impact**: Estimated 20% reduction in fee revenue per transaction
- **Volume Increase**: Expected 30-40% increase in trading volume
- **Net Effect**: Projected 8-20% increase in total revenue
    `,
    proposer: {
      address: '0x1234...5678',
      avatar: 'https://picsum.photos/seed/proposer/100/100',
      votingPower: 250000,
    },
    status: 'ACTIVE' as const,
    votesFor: 1250000,
    votesAgainst: 450000,
    totalVotes: 1700000,
    quorum: 1000000,
    endTime: new Date(Date.now() + 86400000 * 2),
    createdAt: new Date(Date.now() - 86400000 * 5),
    category: 'Economics',
    totalVoters: 156,
    discussionLink: 'https://forum.templum.dao/proposal-42',
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(2)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const formatTimeLeft = (endTime: Date) => {
    const diff = endTime.getTime() - Date.now();
    if (diff <= 0) return 'Voting ended';
    const days = Math.floor(diff / 86400000);
    const hours = Math.floor((diff % 86400000) / 3600000);
    if (days > 0) return `${days} days ${hours} hours left`;
    return `${hours} hours left`;
  };

  const formatTimeAgo = (date: Date) => {
    const diff = Date.now() - date.getTime();
    const hours = Math.floor(diff / 3600000);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  };

  const votePercentage = (proposal.votesFor / proposal.totalVotes) * 100;
  const quorumPercentage = (proposal.totalVotes / proposal.quorum) * 100;
  const quorumMet = proposal.totalVotes >= proposal.quorum;

  const handleVote = (choice: 'for' | 'against') => {
    // In production, this would call the governance contract
    console.log(`Voting ${choice} on proposal ${id}`);
    setHasVoted(true);
  };

  return (
    <div className="min-h-screen py-8">
      <div className="container-custom">
        {/* Back Button */}
        <Link
          to="/governance"
          className="inline-flex items-center space-x-2 text-gray-400 hover:text-white transition-colors mb-8"
        >
          <ArrowLeftIcon className="h-5 w-5" />
          <span>Back to Governance</span>
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Header */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass-card p-6"
            >
              <div className="flex items-center space-x-3 mb-4">
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-500/10 text-green-400">
                  Active
                </span>
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-dark-700 text-gray-400">
                  {proposal.category}
                </span>
              </div>

              <h1 className="text-2xl md:text-3xl font-bold mb-4">{proposal.title}</h1>

              <div className="flex items-center space-x-4 text-sm text-gray-400">
                <div className="flex items-center space-x-2">
                  <img
                    src={proposal.proposer.avatar}
                    alt=""
                    className="w-6 h-6 rounded-full"
                  />
                  <span>by {proposal.proposer.address}</span>
                </div>
                <span>•</span>
                <span>Created {formatTimeAgo(proposal.createdAt)}</span>
              </div>
            </motion.div>

            {/* Tabs */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="glass-card overflow-hidden"
            >
              <div className="flex border-b border-white/10">
                {[
                  { id: 'details', label: 'Details' },
                  { id: 'votes', label: `Votes (${proposal.totalVoters})` },
                  { id: 'discussion', label: 'Discussion' },
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as any)}
                    className={`flex-1 px-4 py-4 text-sm font-medium transition-colors relative ${
                      activeTab === tab.id
                        ? 'text-white'
                        : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    {tab.label}
                    {activeTab === tab.id && (
                      <motion.div
                        layoutId="proposalActiveTab"
                        className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-primary-500 to-accent-500"
                      />
                    )}
                  </button>
                ))}
              </div>

              <div className="p-6">
                {activeTab === 'details' && (
                  <div className="prose prose-invert max-w-none">
                    <div className="whitespace-pre-wrap text-gray-300 leading-relaxed">
                      {proposal.description.split('\n').map((line, index) => {
                        if (line.startsWith('## ')) {
                          return (
                            <h2 key={index} className="text-xl font-bold text-white mt-6 mb-3">
                              {line.replace('## ', '')}
                            </h2>
                          );
                        }
                        if (line.startsWith('- ')) {
                          return (
                            <li key={index} className="text-gray-400 ml-4">
                              {line.replace('- ', '')}
                            </li>
                          );
                        }
                        if (line.match(/^\d+\./)) {
                          return (
                            <li key={index} className="text-gray-400 ml-4 list-decimal">
                              {line.replace(/^\d+\.\s*/, '')}
                            </li>
                          );
                        }
                        if (line.startsWith('**') && line.endsWith('**')) {
                          return (
                            <p key={index} className="font-semibold text-white">
                              {line.replace(/\*\*/g, '')}
                            </p>
                          );
                        }
                        return line ? <p key={index}>{line}</p> : <br key={index} />;
                      })}
                    </div>
                  </div>
                )}

                {activeTab === 'votes' && (
                  <div className="space-y-4">
                    {mockVotes.map((vote) => (
                      <div
                        key={vote.id}
                        className="flex items-center justify-between p-4 rounded-xl bg-dark-700/50"
                      >
                        <div className="flex items-center space-x-3">
                          <img
                            src={vote.voter.avatar}
                            alt=""
                            className="w-10 h-10 rounded-full"
                          />
                          <div>
                            <p className="font-medium">{vote.voter.address}</p>
                            <p className="text-sm text-gray-500">{formatTimeAgo(vote.timestamp)}</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-4">
                          <span className="text-gray-400">{formatNumber(vote.votingPower)} votes</span>
                          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                            vote.choice === 'for'
                              ? 'bg-green-500/20 text-green-400'
                              : 'bg-red-500/20 text-red-400'
                          }`}>
                            {vote.choice === 'for' ? 'For' : 'Against'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {activeTab === 'discussion' && (
                  <div className="text-center py-12">
                    <DocumentTextIcon className="h-16 w-16 mx-auto text-gray-500 mb-4" />
                    <h3 className="text-xl font-semibold mb-2">Join the Discussion</h3>
                    <p className="text-gray-400 mb-6">
                      Continue the conversation on our governance forum
                    </p>
                    <a
                      href={proposal.discussionLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-primary"
                    >
                      Open Forum Discussion
                    </a>
                  </div>
                )}
              </div>
            </motion.div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Voting Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="glass-card p-6"
            >
              <h3 className="text-lg font-semibold mb-4">Cast Your Vote</h3>

              {/* Time Left */}
              <div className="flex items-center space-x-2 text-sm text-gray-400 mb-6">
                <ClockIcon className="h-5 w-5" />
                <span>{formatTimeLeft(proposal.endTime)}</span>
              </div>

              {/* Voting Progress */}
              <div className="mb-6">
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="flex items-center space-x-1 text-green-400">
                    <CheckCircleIcon className="h-4 w-4" />
                    <span>For: {formatNumber(proposal.votesFor)}</span>
                  </span>
                  <span>{votePercentage.toFixed(1)}%</span>
                </div>
                <div className="w-full h-3 bg-dark-700 rounded-full overflow-hidden mb-2">
                  <div
                    className="h-full bg-gradient-to-r from-green-500 to-green-400 rounded-full"
                    style={{ width: `${votePercentage}%` }}
                  />
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center space-x-1 text-red-400">
                    <XCircleIcon className="h-4 w-4" />
                    <span>Against: {formatNumber(proposal.votesAgainst)}</span>
                  </span>
                  <span>{(100 - votePercentage).toFixed(1)}%</span>
                </div>
              </div>

              {/* Quorum Progress */}
              <div className="mb-6 p-4 rounded-xl bg-dark-700/50">
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="text-gray-400">Quorum Progress</span>
                  <span className={quorumMet ? 'text-green-400' : 'text-yellow-400'}>
                    {quorumMet ? 'Reached' : `${quorumPercentage.toFixed(0)}%`}
                  </span>
                </div>
                <div className="w-full h-2 bg-dark-600 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${quorumMet ? 'bg-green-500' : 'bg-yellow-500'}`}
                    style={{ width: `${Math.min(quorumPercentage, 100)}%` }}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {formatNumber(proposal.totalVotes)} / {formatNumber(proposal.quorum)} required
                </p>
              </div>

              {/* Vote Buttons */}
              {!hasVoted ? (
                <div className="space-y-3">
                  <button
                    onClick={() => handleVote('for')}
                    className="w-full py-3 bg-green-500/20 text-green-400 rounded-xl font-medium hover:bg-green-500/30 transition-colors"
                  >
                    Vote For
                  </button>
                  <button
                    onClick={() => handleVote('against')}
                    className="w-full py-3 bg-red-500/20 text-red-400 rounded-xl font-medium hover:bg-red-500/30 transition-colors"
                  >
                    Vote Against
                  </button>
                </div>
              ) : (
                <div className="p-4 rounded-xl bg-primary-500/10 border border-primary-500/20">
                  <div className="flex items-center space-x-2 text-primary-400">
                    <CheckCircleIcon className="h-5 w-5" />
                    <span className="font-medium">Vote Submitted</span>
                  </div>
                </div>
              )}
            </motion.div>

            {/* Info Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="glass-card p-6"
            >
              <h3 className="text-lg font-semibold mb-4">Proposal Info</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between py-2 border-b border-white/5">
                  <span className="text-gray-400">Proposer</span>
                  <span className="font-mono text-sm">{proposal.proposer.address}</span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-white/5">
                  <span className="text-gray-400">Voting Power</span>
                  <span>{formatNumber(proposal.proposer.votingPower)}</span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-white/5">
                  <span className="text-gray-400">Total Voters</span>
                  <span>{proposal.totalVoters}</span>
                </div>
                <div className="flex items-center justify-between py-2">
                  <span className="text-gray-400">Status</span>
                  <span className="px-2 py-1 rounded text-xs font-medium bg-green-500/20 text-green-400">
                    Active
                  </span>
                </div>
              </div>
            </motion.div>

            {/* Execution Info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="glass-card p-6"
            >
              <div className="flex items-start space-x-3">
                <InformationCircleIcon className="h-6 w-6 text-primary-400 flex-shrink-0" />
                <div>
                  <h4 className="font-medium mb-2">Execution Timeline</h4>
                  <ul className="text-sm text-gray-400 space-y-2">
                    <li>• Voting ends in {formatTimeLeft(proposal.endTime)}</li>
                    <li>• 48-hour timelock after passing</li>
                    <li>• Anyone can execute after timelock</li>
                  </ul>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProposalDetailPage;
